import { HttpClient } from '@angular/common/http';
import {
  EnvironmentProviders,
  Injectable,
  Provider,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import {
  Translation,
  TranslocoLoader,
  TranslocoService,
  provideTransloco,
} from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { Observable, firstValueFrom } from 'rxjs';
import { AVAILABLE_LANGS, DEFAULT_LANG, MESSAGEFORMAT_LOCALES, resolveBrowserLang } from './locale';

/**
 * TranslocoHttpLoader — fetches `public/i18n/<lang>.json` at runtime.
 *
 * Runtime JSON catalogues rather than compile-time `$localize`: one build
 * serves every language, adding a language is dropping in a `<lang>.json`,
 * and the keys and tooling match the sibling workplace-settings and wip-map
 * apps.
 */
@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string): Observable<Translation> {
    return this.http.get<Translation>(`/i18n/${lang}.json`);
  }
}

/**
 * App-level Transloco providers. Spread into ApplicationConfig.providers.
 *
 * NOTE: HttpClient is intentionally NOT provided here — `provideGraphQL()`
 * already calls `provideHttpClient()`, and the loader injects that client.
 */
export function provideAppTransloco(): (Provider | EnvironmentProviders)[] {
  return [
    provideTransloco({
      config: {
        availableLangs: AVAILABLE_LANGS,
        defaultLang: DEFAULT_LANG,
        fallbackLang: DEFAULT_LANG,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          logMissingKey: isDevMode(),
          useFallbackTranslation: true,
          allowEmpty: false,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    // ICU messageformat — `{name}` placeholders and plurals, as in the other apps.
    provideTranslocoMessageformat({ locales: MESSAGEFORMAT_LOCALES }),
    // Sign-in comes before any profile, so the browser's language is the only
    // signal. Its catalogue loads before first paint: the auth error copy
    // is built imperatively with `translate()`, which would
    // otherwise race the HTTP load and render keys. If that fails, load the
    // default language so sign-in still works.
    provideAppInitializer(async () => {
      const transloco = inject(TranslocoService);
      const lang = resolveBrowserLang(navigator.languages ?? [navigator.language]);
      transloco.setActiveLang(lang);
      document.documentElement.lang = lang;
      try {
        await firstValueFrom(transloco.load(lang));
      } catch (err) {
        console.warn(`[transloco] loading "${lang}" failed, using the default language:`, err);
        transloco.setActiveLang(DEFAULT_LANG);
        document.documentElement.lang = DEFAULT_LANG;
        await firstValueFrom(transloco.load(DEFAULT_LANG));
      }
    }),
  ];
}
